// client/src/components/pedidos/PedidoForm.js
import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react'; // Adicionado Fragment
import {
    X, Save, Plus, Trash2, Search, Package, Users, MapPin,
    DollarSign, Calendar, Building, Gift, Box, Clock, Loader2, // Adicionado Loader2
    XCircle, AlertCircle, CheckCircle2, Tag, Scan, Calculator, Receipt, Smartphone, Monitor, TrendingUp // Ícones do VendaForm para referência (não usados diretamente aqui)
} from 'lucide-react';
import { useMutatePedidos, usePedido } from '../../hooks/usePedidos';
import { useClientes } from '../../hooks/useClientes';
import { useItensVenda } from '../../hooks/useItensVenda';
import { useServicos } from '../../hooks/useServicos';
import { useRecursos } from '../../hooks/useRecursos';
import { useOfertas } from '../../hooks/useOfertas';
import { usePacotesCliente } from '../../hooks/usePacotesCliente';
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../hooks/useAuth';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';

// Função auxiliar para inicializar o estado do formulário
const getInitialFormData = (current = null) => ({
    cliente: current?.cliente || null,
    itens: Array.isArray(current?.itens) ? current.itens : [],
    tipoEntrega: current?.tipoEntrega || 'retirada',
    enderecoEntrega: current?.enderecoEntrega || null,
    observacoes: current?.observacoes || '',
    observacoesInternas: current?.observacoesInternas || '',
    instrucoesEntrega: current?.enderecoEntrega?.instrucoesEntrega || '',
    descontoGeral: current?.desconto || 0,
});

const PedidoForm = ({ onCloseForm, onSaveSuccess, current }) => {
    const { setAlert } = useAlert();
    const { user } = useAuth();
    const { currency, locale } = useTenantI18n();
    const { createPedido, updatePedido } = useMutatePedidos();
    // O `current` recebido da linha da lista vem com populate raso (sem
    // telefone do cliente, sem itens.unidade, agendamentosRelacionados etc.)
    // — busca dedicada por /pedidos/:id (mesmo padrão do detalhe no mobile)
    // garante os dados completos ao editar.
    const { data: pedidoCompletoResponse } = usePedido(current?._id);
    const pedidoCompleto = pedidoCompletoResponse?.data || current;

    const [formData, setFormData] = useState(() => getInitialFormData(current));
    const [etapaAtual, setEtapaAtual] = useState(current ? 'itens' : 'cliente');
    const [buscaCliente, setBuscaCliente] = useState('');
    const [buscaItem, setBuscaItem] = useState('');
    const [tipoItemSelecionado, setTipoItemSelecionado] = useState('ItemVenda');
    const [itemSelecionado, setItemSelecionado] = useState(null);
    const [quantidadeItem, setQuantidadeItem] = useState(1);
    const [unidadeSelecionada, setUnidadeSelecionada] = useState('');

    // ID da conta do usuário logado — vem do JWT via useAuth
    const accountId = user?.accountId || user?.account?._id || user?.account || '';

    const tiposItens = [
        { value: 'ItemVenda', label: 'Produtos', icon: Package },
        { value: 'Servico', label: 'Serviços', icon: Clock },
        { value: 'Recurso', label: 'Recursos', icon: Building },
        { value: 'Oferta', label: 'Ofertas', icon: Gift },
        { value: 'PacoteCliente', label: 'Pacotes', icon: Box }
    ];

    // --- Lógica de Busca de Dados ---

    // Parâmetros para busca de itens, inclui 'search' condicionalmente
    const hookParams = useMemo(() => ({
        limit: 10, // Limite inicial/busca
        // Inclui 'search' apenas se houver algo digitado
        ...(buscaItem.length > 0 && { search: buscaItem })
        // Se quiser otimizar para buscar só após X chars:
        // ...(buscaItem.length > 2 && { search: buscaItem })
    }), [buscaItem]);

    // Função para verificar se a busca de um tipo de item deve estar ativa
    const isEnabledForCurrentType = useCallback((itemType) => {
        return etapaAtual === 'itens' && tipoItemSelecionado === itemType;
    }, [etapaAtual, tipoItemSelecionado]);

    // Hook para buscar Clientes (ativado na etapa 'cliente' se houver busca)
    const buscarClientes = useClientes({
        search: buscaCliente,
        limit: 5, // Limite menor para clientes
        enabled: etapaAtual === 'cliente' && buscaCliente.length > 0
    });
    const clientes = buscarClientes.data?.data || [];

    // Hooks para buscar cada tipo de Item (chamados incondicionalmente)
    const buscarItensVenda = useItensVenda({
        ...hookParams,
        // account: accountId, // Descomentar se API necessitar
        populate: 'unidadesDetalhes.unidade',
        enabled: isEnabledForCurrentType('ItemVenda')
    });
    const buscarServicos = useServicos({
        ...hookParams,
        account: accountId, // Assumindo que serviços são por conta
        enabled: isEnabledForCurrentType('Servico')
    });
    const buscarRecursos = useRecursos({
        ...hookParams,
        account: accountId, // Assumindo que recursos são por conta
        enabled: isEnabledForCurrentType('Recurso')
    });
    const buscarOfertas = useOfertas({
        ...hookParams,
        // account: accountId, // Descomentar se API necessitar
        enabled: isEnabledForCurrentType('Oferta')
    });
    const buscarPacotes = usePacotesCliente({
        ...hookParams,
        // account: accountId, // Descomentar se API necessitar
        enabled: isEnabledForCurrentType('PacoteCliente')
    });

    // Agrupa os resultados da busca
    const resultadosBusca = useMemo(() => ({
        ItemVenda: buscarItensVenda,
        Servico: buscarServicos,
        Recurso: buscarRecursos,
        Oferta: buscarOfertas,
        PacoteCliente: buscarPacotes,
    }), [buscarItensVenda, buscarServicos, buscarRecursos, buscarOfertas, buscarPacotes]);

    // Seleciona os dados e status da busca ativa
    const buscaAtual = resultadosBusca[tipoItemSelecionado];
    const itens = buscaAtual?.data?.data || []; // Lista de itens encontrados
    const isBuscandoItens = buscaAtual?.isLoading || false; // Status de loading
    const errorBuscandoItens = buscaAtual?.error || null; // Status de erro

    // Repopula o formulário assim que os dados completos do pedido chegam
    // (a primeira renderização usa o `current` resumido da lista).
    useEffect(() => {
        if (current?._id && pedidoCompleto) {
            setFormData(getInitialFormData(pedidoCompleto));
        }
    }, [pedidoCompleto, current?._id]);

    // --- Funções Auxiliares e Callbacks ---

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSelecionarCliente = useCallback((cliente) => {
        setFormData(prev => ({
            ...prev,
            cliente,
            // Cliente.enderecos não existe no schema — o cliente tem um único
            // endereço em `enderecoPessoal` (ver server/models/Cliente.js).
            enderecoEntrega: cliente.enderecoPessoal?.logradouro ? cliente.enderecoPessoal : null
        }));
        setEtapaAtual('itens'); // Avança para a próxima etapa
        setBuscaCliente(''); // Limpa a busca
    }, []);

    // Calcula o preço de UM item (para exibição ou cálculo unitário)
    const calcularPrecoItem = useCallback((item, quantidade = 1) => {
        if (!item || typeof item !== 'object') return 0;
        const tipo = item.modelo || tipoItemSelecionado; // Usa o 'modelo' do item se existir

        switch (tipo) {
            case 'ItemVenda':
            case 'Servico':
                return (Number(item.valorFixo) || 0) * quantidade;
            case 'Recurso':
                const valorHora = Number(item.valorPorHora) || 0;
                // Se for pacote de recurso, pode ter um valor diferente (não tratado aqui)
                return valorHora * quantidade; // Simplificado: Assume sempre por hora
            case 'Oferta':
                // Oferta pode ter valor fixo ou estimado
                return (Number(item.valorFixo) || Number(item.valorTotalEstimado) || 0) * quantidade;
            case 'PacoteCliente':
                 // Pacote do cliente já foi pago, custo no pedido é 0
                return 0;
            default:
                return 0;
        }
    }, [tipoItemSelecionado]); // Depende do tipo SELECIONADO para itens ainda não adicionados

    // Retorna as opções de unidade/estoque para um ItemVenda
    const obterUnidadesDisponiveis = useCallback((item) => {
        if (!item || typeof item !== 'object' || item.modelo !== 'ItemVenda') {
            return [];
        }
        return Array.isArray(item.unidadesDetalhes)
            ? item.unidadesDetalhes
                .filter(ud => ud && (ud.unidade?._id || typeof ud.unidade === 'string'))
                .map(ud => ({
                    id: ud.unidade?._id || ud.unidade,
                    nome: ud.unidade?.nome || 'Unidade sem nome',
                    cidade: ud.unidade?.endereco?.localidade || ud.unidade?.localidade || 'N/A',
                    estoque: ud.quantidadeEmEstoque != null ? Number(ud.quantidadeEmEstoque) : undefined
                }))
            : [];
    }, []); // Não depende mais de tipoItemSelecionado, usa item.modelo

    // Adiciona o item selecionado ao carrinho (formData.itens)
    const handleAdicionarItem = useCallback(() => {
        if (!itemSelecionado || quantidadeItem < 1) {
            return setAlert('Selecione um item e informe uma quantidade válida.', 'error');
        }

        let unidadeId = null;
        const itemModelo = itemSelecionado.modelo || tipoItemSelecionado;

        if (itemModelo === 'ItemVenda') {
            if (!unidadeSelecionada) {
                return setAlert('Selecione uma unidade de estoque para o produto.', 'error');
            }
            unidadeId = unidadeSelecionada;
        } else {
            unidadeId = itemSelecionado.unidade?._id
                     || (typeof itemSelecionado.unidade === 'string' ? itemSelecionado.unidade : null);

            if (!unidadeId) {
                console.error("Item selecionado sem ID de unidade/endereço:", itemSelecionado);
                return setAlert(`Configuração inválida: O item "${itemSelecionado.nome}" (${itemModelo}) não tem uma 'unidade' (Endereço) associada.`, 'error');
            }
        }

        // Calcula preço unitário e total para o item a ser adicionado
        const precoUnitario = calcularPrecoItem(itemSelecionado, 1);
        const totalItem = calcularPrecoItem(itemSelecionado, quantidadeItem);

        // Cria o objeto do item no formato esperado pelo PedidoSchema
        const novoItem = {
            produto: itemSelecionado._id,
            modelo: itemModelo,
            nome: itemSelecionado.nome || `Item ${itemModelo}`,
            descricao: itemSelecionado.descricao || '',
            sku: itemSelecionado.sku || '',
            quantidade: Number(quantidadeItem),
            precoUnitario: Number(precoUnitario) || 0,
            total: Number(totalItem) || 0,
            unidade: unidadeId, // ID do Endereço
            imagemUrl: itemSelecionado.imagemUrl || null,
            personalizacoes: {}, // Futuramente pode ter opções aqui
            status: 'pendente',
            metadata: {
                controlaEstoque: itemModelo === 'ItemVenda' ? Boolean(itemSelecionado.controlaEstoque) : false,
                requerAgendamento: ['Servico', 'Recurso', 'Oferta'].includes(itemModelo) // PacoteCliente também pode requerer? Ajustar se necessário
            }
        };

        // Adiciona o novo item ao estado, garantindo que 'itens' é sempre um array
        setFormData(prev => ({
            ...prev,
            itens: [...(Array.isArray(prev.itens) ? prev.itens : []), novoItem]
        }));

        // Limpa a seleção e busca
        setItemSelecionado(null);
        setQuantidadeItem(1);
        setUnidadeSelecionada('');
        setBuscaItem('');
        setAlert(`${itemModelo} adicionado!`, 'success', 1500); // Feedback curto

    }, [itemSelecionado, quantidadeItem, unidadeSelecionada, tipoItemSelecionado, calcularPrecoItem, setAlert, formData.itens]); // Adicionado formData.itens

    const handleRemoverItem = useCallback((index) => {
        setFormData(prev => {
            const currentItems = Array.isArray(prev.itens) ? prev.itens : [];
            return { ...prev, itens: currentItems.filter((_, i) => i !== index) };
        });
    }, []);

    const handleAtualizarQuantidade = useCallback((index, novaQuantidade) => {
        const qtdFinal = Math.max(1, novaQuantidade); // Mínimo 1

        setFormData(prev => {
            const currentItems = Array.isArray(prev.itens) ? prev.itens : [];
            const novosItens = currentItems.map(it => ({ ...it })); // Copia

            if (index < 0 || index >= novosItens.length) return prev; // Índice inválido

            const item = novosItens[index];
            // Verifica estoque se aplicável
            if (item.modelo === 'ItemVenda' && item.metadata?.controlaEstoque) {
                 // Precisa buscar o estoque atual da unidade específica do item
                 // Esta lógica pode ser complexa aqui, talvez validar apenas no backend?
                 // Por ora, vamos permitir, mas idealmente buscaria o estoque da 'item.unidade'
                 // console.warn("Validação de estoque no frontend não implementada ao atualizar quantidade.");
            }

            item.quantidade = qtdFinal;
            // Recalcula total do item (usa Number() para segurança)
            item.total = (Number(item.precoUnitario) || 0) * qtdFinal;

            return { ...prev, itens: novosItens };
        });
    }, []); // Não depende de 'setAlert' diretamente

     // Calcula subtotal e total GERAL do pedido
    const { subtotal, frete, total } = useMemo(() => {
        const currentItems = Array.isArray(formData.itens) ? formData.itens : [];
        const subtotalCalculado = currentItems.reduce((acc, item) => acc + (Number(item.total) || 0), 0);
        // Lógica de frete placeholder - Idealmente viria da API ou cálculo real
        const freteCalculado = formData.tipoEntrega === 'entrega' ? 10 : 0;
        const totalCalculado = subtotalCalculado + freteCalculado - (Number(formData.descontoGeral) || 0); // Aplica desconto geral aqui

        return {
            subtotal: Math.max(0, subtotalCalculado),
            frete: freteCalculado,
            total: Math.max(0, totalCalculado) // Garante que não seja negativo
        };
    }, [formData.itens, formData.tipoEntrega, formData.descontoGeral]); // Incluído descontoGeral

    // Valida o formulário antes de submeter
    const validarFormulario = useCallback(() => {
        if (!formData.cliente?._id) {
            setAlert('Selecione um cliente válido.', 'error');
            setEtapaAtual('cliente');
            return false;
        }
        const currentItems = Array.isArray(formData.itens) ? formData.itens : [];
        if (currentItems.length === 0) {
            setAlert('Adicione pelo menos um item ao pedido.', 'error');
            setEtapaAtual('itens');
            return false;
        }
        // Validação de unidade já feita ao adicionar, mas checa de novo por segurança
        for (const item of currentItems) {
            if (!item.unidade) {
                setAlert(`Erro interno: O item "${item.nome}" está sem unidade. Remova-o e adicione novamente.`, 'error');
                setEtapaAtual('itens');
                return false;
            }
        }
        if (formData.tipoEntrega === 'entrega' && !formData.enderecoEntrega?.logradouro) {
            setAlert('Selecione um endereço de entrega válido.', 'error');
            setEtapaAtual('entrega');
            return false;
        }
        return true;
    }, [formData, setAlert]);

    // Submete o formulário (Cria ou Atualiza)
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        const currentItems = Array.isArray(formData.itens) ? formData.itens : [];

        // Mapeia o endereço de entrega para o formato do backend
        let enderecoEntregaFinal = null;
        if (formData.tipoEntrega === 'entrega' && formData.enderecoEntrega) {
            const end = formData.enderecoEntrega;
            enderecoEntregaFinal = {
                nome: end.nome || 'Endereço Principal',
                cep: end.cep, logradouro: end.logradouro, numero: end.numero,
                complemento: end.complemento || '', bairro: end.bairro,
                cidade: end.localidade || end.cidade, // Mapeia localidade->cidade
                estado: end.uf || end.estado, // Mapeia uf->estado
                telefone: end.telefone || formData.cliente?.user?.telefone || '',
                instrucoesEntrega: formData.instrucoesEntrega || ''
            };
        }

        // Determina o tipo do pedido
        const temProdutos = currentItems.some(item => item.modelo === 'ItemVenda');
        const temServicos = currentItems.some(item => ['Servico', 'Oferta', 'PacoteCliente'].includes(item.modelo));
        const temRecursos = currentItems.some(item => item.modelo === 'Recurso');
        const qtdTipos = [temProdutos, temServicos, temRecursos].filter(Boolean).length;
        let tipoPedido = 'produtos';
        if (qtdTipos > 1) tipoPedido = 'misto';
        else if (temRecursos) tipoPedido = 'recurso';
        else if (temServicos) tipoPedido = 'servicos';

        // Monta o objeto final para a API
        const pedidoData = {
            cliente: formData.cliente._id,
            itens: currentItems, // Já está no formato correto
            subtotal,
            frete: { valor: frete, metodo: formData.tipoEntrega === 'entrega' ? 'correios' : 'retirada_loja' },
            desconto: Number(formData.descontoGeral) || 0, // Adiciona desconto geral ao payload
            total,
            tipo: tipoPedido,
            tipoEntrega: formData.tipoEntrega,
            enderecoEntrega: enderecoEntregaFinal, // Pode ser null
            observacoes: formData.observacoes,
            observacoesInternas: formData.observacoesInternas,
            // Status inicial pode ser definido aqui se necessário, mas o backend já faz isso
            // status: 'pendente',
            // account: accountId // O backend deve associar a conta pelo usuário logado
        };

        try {
            if (current?._id) { // Se 'current' tem um ID, atualiza
                await updatePedido.mutateAsync({ id: current._id, pedidoData });
            } else { // Senão, cria
                await createPedido.mutateAsync(pedidoData);
            }
            onSaveSuccess(); // Chama o callback de sucesso (fecha form, recarrega lista)
            // O alerta de sucesso já é exibido pelo useMutatePedidos
        } catch (error) {
            console.error('Erro ao salvar pedido:', error);
            // O alerta de erro já é exibido pelo useMutatePedidos
        }
    }, [formData, current, subtotal, frete, total, validarFormulario, createPedido, updatePedido, onSaveSuccess, setAlert]); // Dependências

    // Verifica validade do formulário SEM side-effects (não chama setAlert nem setEtapaAtual)
    // Usado apenas no disabled do botão Salvar para evitar side-effects em render.
    const isFormValid = useMemo(() => {
        if (!formData.cliente?._id) return false;
        const items = Array.isArray(formData.itens) ? formData.itens : [];
        if (items.length === 0) return false;
        if (items.some(item => !item.unidade)) return false;
        if (formData.tipoEntrega === 'entrega' && !formData.enderecoEntrega?.logradouro) return false;
        return true;
    }, [formData]);

    // Calcula o status de loading geral (inclui busca de itens se ativa)
    const isLoading = createPedido.isPending || updatePedido.isPending || buscarClientes.isLoading || (etapaAtual === 'itens' && isBuscandoItens);

    // Calcula as unidades disponíveis para o item selecionado (usado no dropdown)
    const unidadesDisponiveis = useMemo(() =>
        itemSelecionado ? obterUnidadesDisponiveis(itemSelecionado) : [],
    [itemSelecionado, obterUnidadesDisponiveis]);

    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const formatCurrency = (value) => formatCurrencyUtil(value || 0, currency, locale);

    // --- Renderização do Componente (JSX) ---
    return (
        <div className="pedido-form-container">
            {/* Header */}
            <div className="form-header">
                <h2 className="form-title">
                    <Package className="icon-title" />
                    {current ? 'Editar Pedido' : 'Novo Pedido'}
                    {/* Mostra loading geral se alguma operação estiver pendente */}
                    {isLoading && <Loader2 size={18} className="animate-spin ml-2 text-blue-500" />}
                </h2>
                <button onClick={onCloseForm} className="close-button" disabled={isLoading} title="Fechar">
                    <X size={20} />
                </button>
            </div>

            {/* Navegação por Etapas */}
            <div className="etapas-navegacao">
                <button className={`etapa ${etapaAtual === 'cliente' ? 'ativa' : ''} ${!!current ? 'disabled' : ''}`} onClick={() => !current && setEtapaAtual('cliente')} disabled={!!current} > <Users size={16} /> Cliente </button>
                <button className={`etapa ${etapaAtual === 'itens' ? 'ativa' : ''}`} onClick={() => formData.cliente && setEtapaAtual('itens')} disabled={!formData.cliente} > <Package size={16} /> Itens </button>
                <button className={`etapa ${etapaAtual === 'entrega' ? 'ativa' : ''}`} onClick={() => formData.itens.length > 0 && setEtapaAtual('entrega')} disabled={!Array.isArray(formData.itens) || formData.itens.length === 0} > <MapPin size={16} /> Entrega </button>
                <button className={`etapa ${etapaAtual === 'revisao' ? 'ativa' : ''}`} onClick={() => formData.itens.length > 0 && setEtapaAtual('revisao')} disabled={!Array.isArray(formData.itens) || formData.itens.length === 0} > <DollarSign size={16} /> Revisão </button>
            </div>

            {/* Formulário Principal */}
            <form onSubmit={handleSubmit} className="pedido-form">

                {/* --- Conteúdo da Etapa: Cliente --- */}
                {etapaAtual === 'cliente' && (
                    <div className="etapa-conteudo">
                        <h3 className="etapa-titulo">1. Selecionar Cliente</h3>
                        <div className="form-group">
                            <label htmlFor="buscaClienteInput">Buscar Cliente por nome ou email</label>
                            <div className="search-box">
                                <Search size={18} className="search-icon" />
                                <input
                                    id="buscaClienteInput"
                                    type="text"
                                    placeholder="Digite para buscar..."
                                    value={buscaCliente}
                                    onChange={(e) => setBuscaCliente(e.target.value)}
                                    className="search-input"
                                    disabled={!!current} // Desabilita busca se estiver editando
                                />
                                {buscaCliente && <button type="button" onClick={() => setBuscaCliente('')} className="clear-search-button"><X size={16}/></button>}
                            </div>
                        </div>

                        <div className="clientes-lista">
                            {buscarClientes.isLoading && <div className="loading-state"><Loader2 size={16} className="animate-spin mr-2"/> Buscando clientes...</div>}
                            {buscarClientes.error && <div className="error-state"><AlertCircle size={16} className="mr-2"/> Erro ao buscar clientes.</div>}
                            {!buscarClientes.isLoading && !buscarClientes.error && clientes.map(cliente => (
                                <div
                                    key={cliente._id}
                                    className={`cliente-card selectable ${formData.cliente?._id === cliente._id ? 'selected' : ''}`}
                                    onClick={() => !current && handleSelecionarCliente(cliente)} // Permite selecionar só se não estiver editando
                                    role="button" // Melhora acessibilidade
                                    tabIndex={0} // Permite focar com teclado
                                >
                                    <div className="cliente-info">
                                        <h4 className="cliente-nome">{cliente.user?.name || 'Cliente sem nome'}</h4>
                                        <p className="cliente-email">{cliente.user?.email || 'Sem email'}</p>
                                        <p className="cliente-telefone">{cliente.user?.telefone || 'Sem telefone'}</p>
                                    </div>
                                    {/* Mostra contagem de endereços */}
                                    {Array.isArray(cliente.enderecos) && cliente.enderecos.length > 0 && (
                                        <div className="cliente-enderecos">
                                            <span className="address-count-badge">{cliente.enderecos.length} end.</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {!buscarClientes.isLoading && clientes.length === 0 && buscaCliente.length > 0 && (
                                <div className="empty-state"><Users size={32} className="icon"/> <p>Nenhum cliente encontrado para "{buscaCliente}"</p></div>
                            )}
                             {!buscarClientes.isLoading && clientes.length === 0 && buscaCliente.length === 0 && !current &&(
                                <div className="empty-state muted"><p>Digite na busca para encontrar um cliente.</p></div>
                            )}
                        </div>

                        {/* Mostra info do cliente selecionado (ou do pedido atual se editando) */}
                        {formData.cliente && (
                            <div className="cliente-selecionado-info">
                                <h4>Cliente Selecionado:</h4>
                                <p><strong>Nome:</strong> {formData.cliente.user?.name}</p>
                                <p><strong>Email:</strong> {formData.cliente.user?.email}</p>
                                {!current && ( // Botão para alterar só aparece se NÃO estiver editando
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, cliente: null, enderecoEntrega: null }))}
                                        className="action-button action-button-secondary small mt-2"
                                    >
                                        Alterar Cliente
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* --- Conteúdo da Etapa: Itens --- */}
                {etapaAtual === 'itens' && (
                    <div className="etapa-conteudo">
                        <h3 className="etapa-titulo">2. Adicionar Itens</h3>
                        <div className="adicionar-item-panel">
                            {/* Inputs para selecionar tipo, buscar, quantidade e unidade */}
                            <div className="form-grid-container item-selection-controls">
                                <div className="form-group">
                                    <label htmlFor="tipoItemSelect">Tipo de Item</label>
                                    <select
                                        id="tipoItemSelect"
                                        value={tipoItemSelecionado}
                                        onChange={(e) => {
                                            setTipoItemSelecionado(e.target.value);
                                            setItemSelecionado(null);
                                            setUnidadeSelecionada('');
                                            setBuscaItem('');
                                        }}
                                        className="form-input"
                                    >
                                        {tiposItens.map(tipo => (<option key={tipo.value} value={tipo.value}>{tipo.label}</option>))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="buscaItemInput">Buscar {tiposItens.find(t => t.value === tipoItemSelecionado)?.label || 'Item'}</label>
                                    <div className="search-box">
                                        <Search size={18} className="search-icon"/>
                                        <input
                                            id="buscaItemInput"
                                            type="text"
                                            placeholder="Digite o nome para buscar..."
                                            value={buscaItem}
                                            onChange={(e) => setBuscaItem(e.target.value)}
                                            className="search-input"
                                        />
                                         {buscaItem && <button type="button" onClick={() => setBuscaItem('')} className="clear-search-button"><X size={16}/></button>}
                                    </div>
                                </div>
                                <div className="form-group quantidade-group">
                                    <label htmlFor="quantidadeItemInput">Qtd.</label>
                                    <input
                                        id="quantidadeItemInput"
                                        type="number"
                                        min="1"
                                        value={quantidadeItem}
                                        onChange={(e) => setQuantidadeItem(Number(e.target.value) || 1)}
                                        className="form-input"
                                        disabled={!itemSelecionado} // Desabilita se nenhum item selecionado
                                    />
                                </div>
                                {/* Dropdown de Unidade (só para ItemVenda e se houver unidades) */}
                                {tipoItemSelecionado === 'ItemVenda' && itemSelecionado && (
                                    <div className="form-group unidade-group">
                                        <label htmlFor="unidadeItemSelect">Unidade (Estoque)</label>
                                        {unidadesDisponiveis.length > 0 ? (
                                            <select
                                                id="unidadeItemSelect"
                                                value={unidadeSelecionada}
                                                onChange={(e) => setUnidadeSelecionada(e.target.value)}
                                                className="form-input"
                                                required // Marca como obrigatório
                                            >
                                                <option value="">Selecione...</option>
                                                {unidadesDisponiveis.map(unidade => (
                                                    <option key={unidade.id} value={unidade.id} disabled={unidade.estoque === 0}>
                                                        {unidade.nome} - {unidade.cidade}
                                                        {unidade.estoque != null ? ` (Est: ${unidade.estoque})` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <p className="form-text-info">Produto sem unidades de estoque definidas.</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Lista de Itens Encontrados na Busca */}
                            <div className="itens-lista search-results-list">
                                {isBuscandoItens && <div className="loading-state"><Loader2 size={16} className="animate-spin mr-2"/> Buscando...</div>}
                                {errorBuscandoItens && <div className="error-state"><AlertCircle size={16} className="mr-2"/> Erro ao buscar itens. Tente novamente.</div>}

                                {!isBuscandoItens && !errorBuscandoItens && itens.map(item => {
                                    const precoUnit = calcularPrecoItem(item, 1);
                                    const isDisabled = item.modelo === 'PacoteCliente' // Desabilita pacote se já pago (preço 0) ou sem sessões? (ajustar regra)
                                                    || (item.modelo === 'ItemVenda' && item.controlaEstoque && obterUnidadesDisponiveis(item).every(u => u.estoque <= 0)) // Desabilita se todas unidades sem estoque
                                                    || (item.modelo === 'Recurso' && item.status !== 'Ativo')
                                                    || (item.modelo === 'Oferta' && !item.ativo);
                                    return (
                                        <div
                                            key={item._id || item.id} // Usa ID do item como chave
                                            className={`item-card selectable ${itemSelecionado?._id === item._id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                            onClick={() => !isDisabled && setItemSelecionado(item)}
                                            role="button"
                                            tabIndex={isDisabled ? -1 : 0}
                                            aria-disabled={isDisabled}
                                        >
                                            <div className="item-imagem">{item.imagemUrl ? <img src={item.imagemUrl} alt={item.nome}/> : <Package size={24}/>}</div>
                                            <div className="item-info">
                                                <h4 className="item-nome">{item.nome}</h4>
                                                <p className="item-descricao">{item.descricao?.substring(0, 70)}{item.descricao?.length > 70 ? '...' : ''}</p>
                                                <p className={`item-preco ${item.modelo === 'PacoteCliente' ? 'pacote-pago' : ''}`}>
                                                    {item.modelo === 'PacoteCliente' ? 'Já Pago' : formatCurrency(precoUnit)}
                                                </p>
                                                {/* Badges de Estoque/Agendamento */}
                                                {item.modelo === 'ItemVenda' && item.controlaEstoque && <span className="stock-badge">{obterUnidadesDisponiveis(item).reduce((sum, u) => sum + (u.estoque || 0), 0)} em estoque</span>}
                                                {['Servico', 'Recurso', 'Oferta'].includes(item.modelo) && <span className="agendamento-badge">Requer Agendamento</span>}
                                                {isDisabled && <span className="unavailable-badge">Indisponível</span>}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Mensagens de Lista Vazia */}
                                {!isBuscandoItens && !errorBuscandoItens && itens.length === 0 && buscaItem.length > 0 && (
                                    <div className="empty-state"><Package size={32}/> <p>Nenhum item encontrado para "{buscaItem}".</p></div>
                                )}
                                {!isBuscandoItens && !errorBuscandoItens && itens.length === 0 && buscaItem.length === 0 && (
                                    <div className="empty-state muted"><p>Digite na busca ou selecione outro tipo de item.</p></div>
                                )}
                            </div>

                            {/* Botão Adicionar Item */}
                            <button
                                type="button"
                                onClick={handleAdicionarItem}
                                disabled={!itemSelecionado || (tipoItemSelecionado === 'ItemVenda' && !unidadeSelecionada) || isLoading}
                                className="action-button action-button-primary add-item-button"
                            >
                                <Plus size={16} /> Adicionar Item ao Pedido
                            </button>
                        </div>

                        {/* Seção de Itens Já Adicionados ao Pedido */}
                        <div className="itens-pedido added-items-section">
                            <h4>Itens no Pedido ({Array.isArray(formData.itens) ? formData.itens.length : 0})</h4>
                            {Array.isArray(formData.itens) && formData.itens.length > 0 ? (
                                <div className="added-items-list">
                                    {formData.itens.map((item, index) => (
                                        <div key={index} className="item-pedido-card added-item-card">
                                            <div className="item-info">
                                                <h5 className="item-nome">{item.nome}</h5>
                                                {/* <p className="item-descricao">{item.descricao}</p> */}
                                                <div className="item-detalhes">
                                                    <span>{item.modelo}</span>
                                                    {/* Controle de Quantidade */}
                                                    <div className="quantidade-control small">
                                                        <button type="button" onClick={() => handleAtualizarQuantidade(index, item.quantidade - 1)} disabled={item.quantidade <= 1}>-</button>
                                                        <span className="quantidade-value">{item.quantidade}</span>
                                                        <button type="button" onClick={() => handleAtualizarQuantidade(index, item.quantidade + 1)}>+</button>
                                                    </div>
                                                    <span>x {formatCurrency(item.precoUnitario)}</span>
                                                    <span className="item-total">= {formatCurrency(item.total)}</span>
                                                </div>
                                                {item.metadata?.requerAgendamento && <span className="agendamento-badge-small">Requer Agendamento</span>}
                                            </div>
                                            <div className="item-actions">
                                                <button type="button" onClick={() => handleRemoverItem(index)} className="action-icon-button action-icon-delete" title="Remover">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state muted"><p>Nenhum item adicionado ainda.</p></div>
                            )}
                        </div>
                    </div>
                )}

                {/* --- Conteúdo da Etapa: Entrega --- */}
                {etapaAtual === 'entrega' && (
                    <div className="etapa-conteudo">
                        <h3 className="etapa-titulo">3. Configuração de Entrega</h3>
                        <div className="form-grid-container">
                             <div className="form-group">
                                <label htmlFor="tipoEntregaSelect">Tipo de Entrega</label>
                                <select
                                    id="tipoEntregaSelect"
                                    name="tipoEntrega" // Necessário para handleInputChange genérico
                                    value={formData.tipoEntrega}
                                    onChange={(e) => handleInputChange('tipoEntrega', e.target.value)}
                                    className="form-input"
                                >
                                    <option value="retirada">Retirada na Loja/Unidade</option>
                                    <option value="entrega">Entrega no Endereço</option>
                                </select>
                            </div>
                        </div>

                        {/* Endereço de Entrega (se tipoEntrega for 'entrega') — o cliente tem
                            um único endereço cadastrado (Cliente.enderecoPessoal), não uma
                            lista; não há "enderecos" no schema. */}
                        {formData.tipoEntrega === 'entrega' && (
                            <div className="endereco-entrega selection-group">
                                <h4>Endereço de Entrega</h4>
                                {formData.cliente?.enderecoPessoal?.logradouro ? (
                                    <div className="enderecos-lista options-list">
                                        <div
                                            className={`endereco-card selectable option-card ${formData.enderecoEntrega ? 'selected' : ''}`}
                                            onClick={() => handleInputChange('enderecoEntrega', formData.cliente.enderecoPessoal)}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <p className="endereco-completo option-detail">
                                                {formData.cliente.enderecoPessoal.logradouro}, {formData.cliente.enderecoPessoal.numero}
                                                {formData.cliente.enderecoPessoal.complemento ? ` - ${formData.cliente.enderecoPessoal.complemento}` : ''}
                                            </p>
                                            <p className="endereco-cidade option-detail">
                                                {formData.cliente.enderecoPessoal.bairro} - {formData.cliente.enderecoPessoal.localidade}/{formData.cliente.enderecoPessoal.uf}
                                            </p>
                                            <p className="endereco-cep option-detail-small">CEP: {formData.cliente.enderecoPessoal.cep}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="empty-state info"><MapPin size={32}/> <p>O cliente selecionado não possui endereço cadastrado.</p></div>
                                )}
                            </div>
                        )}

                        {/* Campo de Observações do Cliente */}
                        <div className="form-group">
                            <label htmlFor="observacoesInput">Observações do Pedido (Visível ao cliente)</label>
                            <textarea
                                id="observacoesInput"
                                name="observacoes"
                                value={formData.observacoes}
                                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                                placeholder="Alguma instrução especial, ponto de referência, etc."
                                rows="3"
                                className="form-input"
                            />
                        </div>

                        {/* Campo de Instruções de Entrega (se aplicável) */}
                        {formData.tipoEntrega === 'entrega' && (
                            <div className="form-group">
                                <label htmlFor="instrucoesEntregaInput">Instruções para Entrega (Ex: Deixar c/ porteiro)</label>
                                <textarea
                                    id="instrucoesEntregaInput"
                                    name="instrucoesEntrega"
                                    value={formData.instrucoesEntrega}
                                    onChange={(e) => handleInputChange('instrucoesEntrega', e.target.value)}
                                    placeholder="Detalhes importantes para o entregador..."
                                    rows="3"
                                    className="form-input"
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* --- Conteúdo da Etapa: Revisão --- */}
                {etapaAtual === 'revisao' && (
                    <div className="etapa-conteudo">
                        <h3 className="etapa-titulo">4. Revisão e Finalização</h3>
                        <div className="revisao-grid">
                            {/* Card Cliente */}
                            <div className="revisao-card card-cliente">
                                <h4><Users size={16} className="icon"/> Cliente</h4>
                                <div className="revisao-info">
                                    <p><strong>Nome:</strong> {formData.cliente?.user?.name || 'Não selecionado'}</p>
                                    <p><strong>Email:</strong> {formData.cliente?.user?.email || 'N/A'}</p>
                                    <p><strong>Telefone:</strong> {formData.cliente?.user?.telefone || 'N/A'}</p>
                                </div>
                            </div>
                            {/* Card Entrega */}
                            <div className="revisao-card card-entrega">
                                <h4><MapPin size={16} className="icon"/> Entrega</h4>
                                <div className="revisao-info">
                                    <p><strong>Tipo:</strong> {formData.tipoEntrega === 'entrega' ? 'Entrega no Endereço' : 'Retirada na Loja'}</p>
                                    {formData.tipoEntrega === 'entrega' && formData.enderecoEntrega ? (
                                        <>
                                            <p><strong>Endereço:</strong> {formData.enderecoEntrega.logradouro}, {formData.enderecoEntrega.numero}</p>
                                            <p><strong>Cidade:</strong> {formData.enderecoEntrega.localidade}/{formData.enderecoEntrega.uf}</p>
                                            <p><strong>CEP:</strong> {formData.enderecoEntrega.cep}</p>
                                            {formData.instrucoesEntrega && <p><strong>Instruções:</strong> {formData.instrucoesEntrega}</p>}
                                        </>
                                    ) : formData.tipoEntrega === 'entrega' ? (
                                         <p className="text-red-600">Endereço não selecionado!</p>
                                    ) : null}
                                </div>
                            </div>
                            {/* Card Resumo Financeiro */}
                            <div className="revisao-card card-financeiro">
                                <h4><DollarSign size={16} className="icon"/> Resumo Financeiro</h4>
                                <div className="revisao-info">
                                    <div className="resumo-linha"><span>Subtotal Itens:</span> <span>{formatCurrency(subtotal)}</span></div>
                                    <div className="resumo-linha"><span>Frete:</span> <span>{formatCurrency(frete)}</span></div>
                                    {/* Input Desconto Geral na Revisão */}
                                    <div className="form-group-inline resumo-linha">
                                        <label htmlFor="descontoGeralRevisao">Desconto Geral (R$):</label>
                                        <input
                                            id="descontoGeralRevisao"
                                            type="number"
                                            name="descontoGeral" // Nome igual ao do estado
                                            value={formData.descontoGeral}
                                            onChange={(e) => handleInputChange('descontoGeral', Number(e.target.value) || 0)}
                                            min="0"
                                            step="0.01"
                                            max={subtotal + frete} // Não pode ser maior que subtotal+frete
                                            className="form-input small-input"
                                         />
                                    </div>
                                    <div className="resumo-linha total"><span>Total a Pagar:</span> <span className="total-amount">{formatCurrency(total)}</span></div>
                                </div>
                            </div>
                        </div>

                         {/* Card Itens do Pedido */}
                        <div className="revisao-card card-itens">
                             <h4><Package size={16} className="icon"/> Itens do Pedido ({Array.isArray(formData.itens) ? formData.itens.length : 0})</h4>
                             <div className="itens-resumo">
                                 {(Array.isArray(formData.itens) ? formData.itens : []).map((item, index) => (
                                     <div key={index} className="item-resumo">
                                         <div className="item-info">
                                             <span className="item-nome">{item.nome}</span>
                                             <span className="item-tipo">{item.modelo}</span>
                                             <span className="item-quantidade">x{item.quantidade}</span>
                                         </div>
                                         <span className="item-total">{formatCurrency(item.total)}</span>
                                     </div>
                                 ))}
                                  {(!Array.isArray(formData.itens) || formData.itens.length === 0) && <p className="text-center text-gray-500 my-4">Nenhum item.</p>}
                             </div>
                        </div>

                         {/* Observações Internas */}
                        <div className="form-group mt-4">
                            <label htmlFor="observacoesInternasInput">Observações Internas (Não visível ao cliente)</label>
                            <textarea
                                id="observacoesInternasInput"
                                name="observacoesInternas"
                                value={formData.observacoesInternas}
                                onChange={(e) => handleInputChange('observacoesInternas', e.target.value)}
                                placeholder="Anotações para a equipe, detalhes do pedido..."
                                rows="3"
                                className="form-input"
                            />
                        </div>
                    </div>
                )}

                {/* --- Ações do Formulário (Botões) --- */}
                <div className="form-actions">
                    <div className="actions-left">
                        {/* Botão Voltar (aparece se não for a primeira etapa) */}
                        {etapaAtual !== 'cliente' && (
                            <button
                                type="button"
                                onClick={() => {
                                    const etapas = ['cliente', 'itens', 'entrega', 'revisao'];
                                    const indexAtual = etapas.indexOf(etapaAtual);
                                    if (indexAtual > 0) {
                                        // Se editando, não volta para cliente a partir de itens
                                        if (indexAtual === 1 && current) return;
                                        setEtapaAtual(etapas[indexAtual - 1]);
                                    }
                                }}
                                className="action-button action-button-secondary"
                                disabled={isLoading}
                            >
                                Voltar
                            </button>
                        )}
                    </div>
                    <div className="actions-right">
                        {/* Botão Cancelar */}
                        <button
                            type="button"
                            onClick={onCloseForm}
                            className="action-button action-button-secondary"
                            disabled={isLoading}
                        >
                            Cancelar
                        </button>
                        {/* Botão Próximo (se não for a última etapa) */}
                        {etapaAtual !== 'revisao' ? (
                            <button
                                type="button"
                                onClick={() => {
                                    // Validações rápidas antes de avançar
                                    if (etapaAtual === 'cliente' && !formData.cliente) { setAlert('Selecione um cliente.', 'warning'); return; }
                                    if (etapaAtual === 'itens' && (!Array.isArray(formData.itens) || formData.itens.length === 0)) { setAlert('Adicione itens ao pedido.', 'warning'); return; }
                                    if (etapaAtual === 'entrega' && formData.tipoEntrega === 'entrega' && !formData.enderecoEntrega) { setAlert('Selecione um endereço.', 'warning'); return; }

                                    const etapas = ['cliente', 'itens', 'entrega', 'revisao'];
                                    const indexAtual = etapas.indexOf(etapaAtual);
                                    if (indexAtual < etapas.length - 1) {
                                        setEtapaAtual(etapas[indexAtual + 1]);
                                    }
                                }}
                                className="action-button action-button-primary"
                                disabled={ // Desabilita se a condição para avançar não for atendida
                                    (etapaAtual === 'cliente' && !formData.cliente) ||
                                    (etapaAtual === 'itens' && (!Array.isArray(formData.itens) || formData.itens.length === 0)) ||
                                    (etapaAtual === 'entrega' && formData.tipoEntrega === 'entrega' && !formData.enderecoEntrega) ||
                                    isLoading
                                }
                            >
                                Próximo
                            </button>
                        ) : (
                            // Botão Salvar (na última etapa)
                            <button
                                type="submit"
                                disabled={isLoading || !isFormValid}
                                className="action-button action-button-success"
                            >
                                {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                {isLoading ? 'Salvando...' : (current ? 'Atualizar Pedido' : 'Criar Pedido')}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PedidoForm;